// src/components/vehicles/VehicleList.js
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { Calendar } from "primereact/calendar";
import { Toast } from "primereact/toast";
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
} from "recharts";
import { api } from "../../utils/api";
import "../../css/Fleet.css";

const COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#06b6d4", "#f97316"];

const VehicleList = () => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [selectedType, setSelectedType] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [summary, setSummary] = useState(null);
  const [form, setForm] = useState({
    vehicleTypeId: null, brand: "", model: "", registrationNumber: "",
    vin: "", registrationDate: null, companyId: null, notes: "", driverOperator: "",
  });
  const toast = useRef(null);
  const navigate = useNavigate();
  const limit = 15;

  useEffect(() => { loadDictionaries(); loadSummary(); }, []);
  useEffect(() => { loadVehicles(); }, [page, search, selectedType, selectedCompany, selectedStatus]);

  const loadDictionaries = async () => {
    try {
      const [typesRes, companiesRes] = await Promise.all([
        api.get("/vehicle-types"), api.get("/vehicle-companies"),
      ]);
      setVehicleTypes(Array.isArray(typesRes.data) ? typesRes.data : []);
      setCompanies(Array.isArray(companiesRes.data) ? companiesRes.data : []);
    } catch (err) { console.error(err); }
  };

  const loadSummary = async () => {
    try {
      const res = await api.get("/vehicles-summary");
      setSummary(res.data);
    } catch (err) { console.error(err); }
  };

  const loadVehicles = async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (search) params.search = search;
      if (selectedType) params.typeId = selectedType;
      if (selectedCompany) params.companyId = selectedCompany;
      if (selectedStatus) params.status = selectedStatus;
      const res = await api.get("/vehicles", { params });
      setVehicles(Array.isArray(res.data?.data) ? res.data.data : []);
      setTotalRecords(res.data?.total || 0);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const openNew = () => {
    setEditingVehicle(null);
    setForm({ vehicleTypeId: null, brand: "", model: "", registrationNumber: "", vin: "", registrationDate: null, companyId: null, notes: "", driverOperator: "" });
    setDialogVisible(true);
  };

  const openEdit = (vehicle) => {
    setEditingVehicle(vehicle);
    setForm({
      vehicleTypeId: vehicle.vehicle_type_id, brand: vehicle.brand, model: vehicle.model,
      registrationNumber: vehicle.registration_number, vin: vehicle.vin || "",
      registrationDate: vehicle.registration_date ? new Date(vehicle.registration_date) : null,
      companyId: vehicle.company_id, notes: vehicle.notes || "", driverOperator: vehicle.driver_operator || "",
    });
    setDialogVisible(true);
  };

  const saveVehicle = async () => {
    if (!form.vehicleTypeId || !form.brand || !form.model || !form.registrationNumber) {
      toast.current.show({ severity: "warn", summary: "Uwaga", detail: "Wypełnij wymagane pola" });
      return;
    }
    try {
      if (editingVehicle) {
        await api.put(`/vehicles/${editingVehicle.id}`, form);
        toast.current.show({ severity: "success", summary: "Sukces", detail: "Pojazd zaktualizowany" });
      } else {
        await api.post("/vehicles", form);
        toast.current.show({ severity: "success", summary: "Sukces", detail: "Pojazd dodany" });
      }
      setDialogVisible(false);
      loadVehicles();
      loadSummary();
    } catch (err) {
      console.error(err);
      toast.current.show({ severity: "error", summary: "Błąd", detail: "Nie udało się zapisać" });
    }
  };

  const deactivateVehicle = async (id) => {
    if (!window.confirm("Czy na pewno chcesz dezaktywować ten pojazd?")) return;
    try {
      await api.delete(`/vehicles/${id}`);
      toast.current.show({ severity: "success", summary: "Sukces", detail: "Pojazd dezaktywowany" });
      loadVehicles();
      loadSummary();
    } catch (err) { console.error(err); }
  };

  const statusBodyTemplate = (rowData) => {
    const active = rowData.status === "ACTIVE";
    return (
      <span className={`fleet-tag ${active ? "fleet-tag--success" : "fleet-tag--danger"}`}>
        <span className={`fleet-status-dot ${active ? "fleet-status-dot--active" : "fleet-status-dot--inactive"}`} />
        {active ? "Aktywny" : "Nieaktywny"}
      </span>
    );
  };

  const actionsBodyTemplate = (rowData) => (
    <div className="fleet-actions">
      <Button icon="pi pi-eye" rounded text severity="info" onClick={() => navigate(`/pojazdy/${rowData.id}`)} tooltip="Szczegóły" tooltipOptions={{ position: "top" }} />
      <Button icon="pi pi-pencil" rounded text severity="warning" onClick={() => openEdit(rowData)} tooltip="Edytuj" tooltipOptions={{ position: "top" }} />
      {rowData.status === "ACTIVE" && (
        <Button icon="pi pi-trash" rounded text severity="danger" onClick={() => deactivateVehicle(rowData.id)} tooltip="Dezaktywuj" tooltipOptions={{ position: "top" }} />
      )}
    </div>
  );

  const regBodyTemplate = (rowData) => (
    <span className="fleet-vehicle-link" onClick={() => navigate(`/pojazdy/${rowData.id}`)}>
      {rowData.registration_number}
    </span>
  );

  const onPage = (e) => setPage(Math.floor(e.first / limit) + 1);

  const statusOptions = [
    { label: "Wszystkie", value: null },
    { label: "Aktywne", value: "ACTIVE" },
    { label: "Nieaktywne", value: "INACTIVE" },
  ];

  const customTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="fleet-tooltip">
          <p style={{ margin: 0, fontWeight: 600, fontSize: "0.85rem" }}>{payload[0].name}</p>
          <p style={{ margin: 0, fontSize: "0.8rem", color: "var(--fleet-text-secondary)" }}>{payload[0].value} pojazdów</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="fleet-page fleet-animate-in">
      <Toast ref={toast} />

      {/* Header */}
      <div className="fleet-page-header">
        <div>
          <h1><span className="fleet-header-icon">🚗</span>Lista pojazdów</h1>
          <p className="fleet-page-subtitle">Zarządzaj flotą pojazdów ({totalRecords} łącznie)</p>
        </div>
        <Button label="Dodaj pojazd" icon="pi pi-plus" className="fleet-btn-primary" onClick={openNew} />
      </div>

      {/* Summary Stat Cards */}
      {summary && (
        <div className="fleet-stats-grid fleet-stats-grid--4">
          <div className="fleet-stat-card fleet-stat-card--primary">
            <div className="fleet-stat-icon fleet-stat-icon--primary"><i className="pi pi-car" /></div>
            <div className="fleet-stat-value fleet-stat-value--primary">{summary.active}</div>
            <div className="fleet-stat-label">Aktywne pojazdy</div>
            {summary.inactive > 0 && (
              <div className="fleet-stat-sub">+ {summary.inactive} nieaktywnych</div>
            )}
          </div>

          <div className="fleet-stat-card fleet-stat-card--danger">
            <div className="fleet-stat-icon fleet-stat-icon--danger"><i className="pi pi-exclamation-triangle" /></div>
            <div className="fleet-stat-value fleet-stat-value--danger">{summary.overdueVehicles}</div>
            <div className="fleet-stat-label">Przeterminowane serwisy</div>
            <div className="fleet-stat-sub">pojazdy z przetermin. term.</div>
          </div>

          <div className="fleet-stat-card fleet-stat-card--warning">
            <div className="fleet-stat-icon fleet-stat-icon--warning"><i className="pi pi-clock" /></div>
            <div className="fleet-stat-value fleet-stat-value--warning">{summary.warningVehicles}</div>
            <div className="fleet-stat-label">Zbliżające się terminy</div>
            <div className="fleet-stat-sub">wymaga uwagi</div>
          </div>

          <div className="fleet-stat-card fleet-stat-card--success">
            <div className="fleet-stat-icon fleet-stat-icon--success"><i className="pi pi-plus-circle" /></div>
            <div className="fleet-stat-value fleet-stat-value--success">{summary.recentlyAdded}</div>
            <div className="fleet-stat-label">Dodane (30 dni)</div>
            <div className="fleet-stat-sub">nowe pojazdy</div>
          </div>
        </div>
      )}

      {/* Type Distribution Chart */}
      {summary?.byType?.length > 0 && (
        <div className="fleet-compact-chart-row">
          <div className="fleet-chart-card fleet-chart-card--compact">
            <div className="fleet-chart-title"><i className="pi pi-chart-pie" /> Rozkład wg typu</div>
            <div className="fleet-chart-with-legend">
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={summary.byType} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={70} innerRadius={35}>
                    {summary.byType.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip content={customTooltip} />
                </PieChart>
              </ResponsiveContainer>
              <div className="fleet-chart-legend-vertical">
                {summary.byType.map((t, i) => (
                  <div key={i} className="fleet-legend-item">
                    <span className="fleet-legend-dot" style={{ background: COLORS[i % COLORS.length] }} />
                    <span className="fleet-legend-label">{t.name}</span>
                    <span className="fleet-legend-value">{t.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="fleet-filters">
        <span className="p-input-icon-left">
          <i className="pi pi-search" />
          <InputText
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Szukaj po marce, modelu, nr rej, VIN..."
            className="fleet-search-input"
          />
        </span>
        <Dropdown value={selectedType} options={vehicleTypes || []} onChange={(e) => { setSelectedType(e.value); setPage(1); }} optionLabel="name" optionValue="id" placeholder="Typ pojazdu" showClear />
        <Dropdown value={selectedCompany} options={companies || []} onChange={(e) => { setSelectedCompany(e.value); setPage(1); }} optionLabel="name" optionValue="id" placeholder="Firma" showClear />
        <Dropdown value={selectedStatus} options={statusOptions} onChange={(e) => { setSelectedStatus(e.value); setPage(1); }} placeholder="Status" />
      </div>

      {/* Table */}
      <div className="fleet-card">
        <div className="fleet-card-body--flush fleet-table">
          <DataTable
            value={vehicles}
            loading={loading}
            paginator rows={limit}
            totalRecords={totalRecords}
            lazy first={(page - 1) * limit}
            onPage={onPage}
            emptyMessage="Brak pojazdów spełniających kryteria"
            responsiveLayout="stack" breakpoint="768px"
          >
            <Column header="Nr rejestracyjny" body={regBodyTemplate} sortable sortField="registration_number" />
            <Column field="brand" header="Marka" sortable />
            <Column field="model" header="Model" sortable />
            <Column field="vehicleTypeName" header="Typ" sortable />
            <Column field="companyName" header="Firma" sortable />
            <Column header="Kierowca/Operator" body={(row) => row.driver_operator || "—"} />
            <Column header="Status" body={statusBodyTemplate} style={{ width: "130px" }} />
            <Column header="Akcje" body={actionsBodyTemplate} style={{ width: "140px" }} />
          </DataTable>
        </div>
      </div>

      {/* Add / Edit Dialog */}
      <Dialog
        header={editingVehicle ? "Edytuj pojazd" : "Nowy pojazd"}
        visible={dialogVisible}
        style={{ width: "620px", maxWidth: "95vw" }}
        onHide={() => setDialogVisible(false)}
        className="fleet-dialog"
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
            <Button label="Anuluj" icon="pi pi-times" className="p-button-text" onClick={() => setDialogVisible(false)} />
            <Button label="Zapisz" icon="pi pi-check" className="fleet-btn-primary" onClick={saveVehicle} />
          </div>
        }
      >
        <div className="p-fluid" style={{ display: "flex", flexDirection: "column", gap: "1.1rem" }}>
          <div className="fleet-form-field">
            <label>Typ pojazdu *</label>
            <Dropdown value={form.vehicleTypeId} options={vehicleTypes || []} onChange={(e) => setForm({ ...form, vehicleTypeId: e.value })} optionLabel="name" optionValue="id" placeholder="Wybierz typ" />
          </div>
          <div className="fleet-form-grid">
            <div className="fleet-form-field">
              <label>Marka *</label>
              <InputText value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} />
            </div>
            <div className="fleet-form-field">
              <label>Model *</label>
              <InputText value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} />
            </div>
          </div>
          <div className="fleet-form-grid">
            <div className="fleet-form-field">
              <label>Nr rejestracyjny *</label>
              <InputText value={form.registrationNumber} onChange={(e) => setForm({ ...form, registrationNumber: e.target.value })} />
            </div>
            <div className="fleet-form-field">
              <label>VIN</label>
              <InputText value={form.vin} onChange={(e) => setForm({ ...form, vin: e.target.value })} maxLength={17} />
            </div>
          </div>
          <div className="fleet-form-grid">
            <div className="fleet-form-field">
              <label>Data rejestracji</label>
              <Calendar value={form.registrationDate} onChange={(e) => setForm({ ...form, registrationDate: e.value })} dateFormat="dd.mm.yy" showIcon />
            </div>
            <div className="fleet-form-field">
              <label>Firma</label>
              <Dropdown value={form.companyId} options={companies || []} onChange={(e) => setForm({ ...form, companyId: e.value })} optionLabel="name" optionValue="id" placeholder="Wybierz firmę" showClear />
            </div>
          </div>
          <div className="fleet-form-field">
            <label>Kierowca / Operator</label>
            <InputText value={form.driverOperator} onChange={(e) => setForm({ ...form, driverOperator: e.target.value })} placeholder="Imię i nazwisko" />
          </div>
          <div className="fleet-form-field">
            <label>Notatki</label>
            <InputTextarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default VehicleList;
